import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  useExportSocialPost,
  useGenerateSocialPost,
  useMarkSocialPostPosted,
} from "../../hooks/useAdminSocial";
import EvidencePanel from "./EvidencePanel";
import PlatformPreview from "./PlatformPreview";

const STATS_FAN_ORIGIN = "https://stats.fan";
const REQUIRED_UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"];
const REWRITE_TONES = [
  ["Shorter", "shorter"],
  ["More analytical", "more_analytical"],
  ["More conversational", "more_conversational"],
  ["More provocative", "more_provocative"],
  ["New hook", "new_hook"],
  ["New CTA", "new_cta"],
];

const selectedVariantFor = (post) => {
  const variants = post?.variants || [];
  return (
    variants.find(({ id }) => id === post.selectedVariantId) ||
    variants[variants.length - 1] ||
    null
  );
};

const buildPostText = (variant) =>
  [variant?.hook, variant?.body, variant?.cta].filter(Boolean).join("\n\n");

const buildDestinationUrl = ({ destinationUrl, utm }) => {
  const destination = new URL(destinationUrl || "", STATS_FAN_ORIGIN);
  if (!destinationUrl || destination.pathname === "/") {
    throw new Error("A non-homepage destination is required before copying.");
  }
  if (
    !REQUIRED_UTM_KEYS.every((key) => String(utm?.[key] || "").trim())
  ) {
    throw new Error(
      "utm_source, utm_medium, and utm_campaign are required before copying."
    );
  }
  const url = new URL(
    `${destination.pathname}${destination.search}${destination.hash}`,
    STATS_FAN_ORIGIN
  );
  Object.entries(utm || {}).forEach(([key, value]) => {
    if (key.startsWith("utm_") && value) url.searchParams.set(key, value);
  });
  return url.toString();
};

const useComposerActions = ({ platform, post }) => {
  const [actionError, setActionError] = useState(null);
  const generate = useGenerateSocialPost();
  const exporter = useExportSocialPost();
  const posted = useMarkSocialPostPosted();
  const actionsDisabled =
    generate.isLoading || exporter.isLoading || posted.isLoading;

  const runGenerate = async (tone = "conversational") => {
    setActionError(null);
    const result = await generate.generatePost({
      id: post.postId,
      platform,
      tone,
    });
    if (!result.success) setActionError(result.error);
  };

  const exportCurrent = async () => {
    setActionError(null);
    const result = await exporter.exportPost({ id: post.postId, platform });
    if (!result.success) {
      setActionError(result.error);
      return null;
    }
    return result.data;
  };

  const copyText = async () => {
    const exported = await exportCurrent();
    if (exported?.variant) {
      await navigator.clipboard.writeText(buildPostText(exported.variant));
    }
  };

  const copyDestination = async () => {
    const exported = await exportCurrent();
    if (exported) {
      let destinationUrl;
      try {
        destinationUrl = buildDestinationUrl(exported);
      } catch (error) {
        setActionError(error.message);
        return;
      }
      await navigator.clipboard.writeText(destinationUrl);
    }
  };

  const markPosted = async () => {
    if (!window.confirm("Mark this post as posted?")) return;
    setActionError(null);
    const result = await posted.markPostPosted(post.postId);
    if (!result.success) setActionError(result.error);
  };

  return {
    actionError,
    actionsDisabled,
    copyDestination,
    copyText,
    markPosted,
    runGenerate,
  };
};

const SocialComposer = ({ post, snapshot }) => {
  const [platform, setPlatform] = useState("x");
  const selectedVariant = selectedVariantFor(post);
  const actions = useComposerActions({ platform, post });

  return (
    <Box aria-label="Social composer">
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h5" fontWeight={800}>
            Composer
          </Typography>
          <Chip label={post.status} size="small" />
        </Stack>
        {actions.actionError && (
          <Alert severity="error">{actions.actionError}</Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <EvidencePanel
              evidence={snapshot?.evidence}
              capturedAt={snapshot?.capturedAt}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              <PlatformPreview
                platform={platform}
                onPlatformChange={setPlatform}
                variant={selectedVariant}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  disabled={actions.actionsDisabled}
                  onClick={() => actions.runGenerate()}
                  variant="contained"
                >
                  Generate
                </Button>
                {REWRITE_TONES.map(([label, tone]) => (
                  <Button
                    key={tone}
                    disabled={actions.actionsDisabled}
                    onClick={() => actions.runGenerate(tone)}
                    variant="outlined"
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  disabled={actions.actionsDisabled}
                  onClick={actions.copyText}
                >
                  Copy text
                </Button>
                <Button
                  disabled={actions.actionsDisabled}
                  onClick={actions.copyDestination}
                >
                  Copy destination + UTM
                </Button>
                <Button
                  color="success"
                  disabled={
                    actions.actionsDisabled || post.status === "posted"
                  }
                  onClick={actions.markPosted}
                  variant="contained"
                >
                  Mark posted
                </Button>
              </Stack>
              <Typography color="text.secondary" variant="body2">
                Graphic recipes later. Copy/export is the v1 path.
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default SocialComposer;
